import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Link2, Zap, BarChart3, Shield, ArrowRight, Phone, Users } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (response.ok) navigate("/dashboard", { replace: true });
      } catch (error) {}
      finally { setIsLoading(false); }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-sm">M</span>
            </div>
            <span className="font-heading text-xl md:text-2xl font-black tracking-tight">
              miwap.link
            </span>
          </div>
          <Button 
            onClick={handleLogin}
            className="bg-black text-white hover:bg-primary rounded-full px-4 md:px-6 text-sm"
            data-testid="login-button"
          >
            Iniciar Sesión
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-16 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-primary font-semibold text-sm md:text-base mb-3 md:mb-4">ROTADOR DE WHATSAPP INTELIGENTE</p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-black leading-tight mb-4 md:mb-6">
                Un enlace.<br />
                <span className="text-primary">Múltiples destinos.</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 max-w-xl">
                Distribuye tu tráfico entre varios números de WhatsApp o grupos. 
                Rotación automática. Control total. Sin complicaciones.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button 
                  onClick={handleLogin}
                  size="lg"
                  className="bg-primary text-white hover:bg-black rounded-full px-6 md:px-8 h-11 md:h-12 text-sm md:text-base"
                  data-testid="hero-cta-button"
                >
                  Comenzar Gratis
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
              
              {/* Example URL */}
              <div className="mt-8 md:mt-10 p-4 bg-zinc-50 border border-border rounded-lg inline-block">
                <p className="text-xs text-muted-foreground mb-2">Tu enlace se verá así:</p>
                <code className="text-lg md:text-xl font-mono font-bold text-primary">miwap.link/mi-equipo-ventas</code>
              </div>
            </div>
            
            <div className="lg:col-span-5">
              <div className="bg-black p-6 md:p-8 rounded-2xl shadow-2xl">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-white">
                    <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-white/60 uppercase tracking-wider">Redirección</p>
                      <p className="text-2xl font-heading font-black">&lt;50ms</p>
                    </div>
                  </div>
                  <div className="h-px bg-white/20"></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-white/60 uppercase tracking-wider">Destinos</p>
                      <p className="text-xl font-heading font-black text-white">Hasta 100</p>
                    </div>
                    <div>
                      <p className="text-xs text-white/60 uppercase tracking-wider">Tipos</p>
                      <p className="text-xl font-heading font-black text-primary">3</p>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <div className="flex items-center gap-1 text-xs text-white/70 bg-white/10 px-2 py-1 rounded">
                      <Phone className="w-3 h-3" /> WhatsApp
                    </div>
                    <div className="flex items-center gap-1 text-xs text-white/70 bg-white/10 px-2 py-1 rounded">
                      <Users className="w-3 h-3" /> Grupos
                    </div>
                    <div className="flex items-center gap-1 text-xs text-white/70 bg-white/10 px-2 py-1 rounded">
                      <Link2 className="w-3 h-3" /> URLs
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-zinc-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <p className="text-primary font-semibold text-sm mb-3">CARACTERÍSTICAS</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black">
              Todo lo que necesitas
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: Zap, title: "Ultra Rápido", desc: "Redirección instantánea. Sin páginas intermedias." },
              { icon: BarChart3, title: "Distribución Inteligente", desc: "Round Robin o porcentajes personalizados." },
              { icon: Shield, title: "Kill Switch", desc: "Desactiva destinos al instante si hay problemas." },
              { icon: Phone, title: "WhatsApp Ready", desc: "Mensajes predefinidos para cada vendedor." }
            ].map((feature, i) => (
              <div key={i} className="bg-white border border-border p-5 md:p-6 rounded-xl hover:border-primary transition-colors">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-black rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-5 h-5 md:w-6 md:h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-base md:text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-primary font-semibold text-sm mb-3">CÓMO FUNCIONA</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black">
              3 pasos simples
            </h2>
          </div>
          
          <div className="space-y-6 md:space-y-8">
            {[
              { num: "01", title: "Crea tu enlace", desc: "Elige un nombre memorable como miwap.link/mi-tienda" },
              { num: "02", title: "Agrega destinos", desc: "Vincula números de WhatsApp, grupos o URLs" },
              { num: "03", title: "Comparte y listo", desc: "Los clics se distribuyen automáticamente" }
            ].map((step, i) => (
              <div key={i} className="flex gap-4 md:gap-6 items-start">
                <div className="w-12 h-12 md:w-16 md:h-16 bg-primary text-white rounded-full flex items-center justify-center font-heading font-black text-lg md:text-xl flex-shrink-0">
                  {step.num}
                </div>
                <div className="pt-1 md:pt-3">
                  <h3 className="font-heading font-bold text-lg md:text-xl mb-1">{step.title}</h3>
                  <p className="text-muted-foreground text-sm md:text-base">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 px-4 md:px-6 bg-black text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-black mb-4 md:mb-6">
            Empieza a distribuir tu tráfico <span className="text-primary">ahora</span>
          </h2>
          <p className="text-white/70 mb-6 md:mb-8 text-sm md:text-base">
            Gratis. Sin tarjeta. Configura en menos de 2 minutos.
          </p>
          <Button 
            onClick={handleLogin}
            size="lg"
            className="bg-primary text-white hover:bg-white hover:text-black rounded-full px-8 md:px-12 h-12 md:h-14 text-base md:text-lg"
          >
            Crear Cuenta Gratis
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 md:py-8 px-4 md:px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-primary rounded flex items-center justify-center">
              <span className="text-white font-black text-xs">M</span>
            </div>
            <span className="font-heading text-sm font-bold">miwap.link</span>
          </div>
          <p className="text-xs md:text-sm text-muted-foreground">
            © 2025 miwap.link - Rotador de WhatsApp Inteligente
          </p>
        </div>
      </footer>
    </div>
  );
}
